"use client";

import { cn } from "@newxsystems/shared";
import { useEffect, useRef } from "react";
import type { IHeroSurfaceProps } from "./HeroSurface.props";

/**
 * A hero photograph rendered as a physical surface: a probe lens under the
 * cursor, a wake that only rings while the pointer is moving, and a chromatic
 * trail that follows scroll. Every signal decays, so the surface comes to rest
 * as a crisp, correctly graded photograph.
 *
 * The surface takes its box from the nearest `[data-hero-root]` and its
 * photograph from the `[data-hero-media] img` inside it, so a hero can hand it
 * a full-bleed background or a single framed image.
 *
 * Falls back to the plain <img> when WebGL2 is unavailable or reduced motion is
 * requested: the canvas stays transparent and the image is never hidden.
 */
const VERTEX_SHADER = `#version 300 es
in vec2 aPosition;
out vec2 vUv;

void main() {
  // vUv is top-left origin, matching DOM coordinates.
  vUv = vec2(aPosition.x * 0.5 + 0.5, 0.5 - aPosition.y * 0.5);
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform sampler2D uPhoto;
uniform vec2 uCanvas;
uniform vec2 uImage;
uniform vec2 uPointer;
uniform float uPointerActive;
uniform float uReach;
uniform float uStir;
uniform float uStrength;
uniform float uSaturation;
uniform float uVelocity;
uniform float uVoidBlend;
uniform float uTime;

in vec2 vUv;
out vec4 outColor;

const vec3 VOID = vec3(8.0, 9.0, 11.0) / 255.0;

// Reproduce object-fit: cover at the default centre position.
vec2 coverUv(vec2 uv) {
  float canvasAspect = uCanvas.x / uCanvas.y;
  float imageAspect = uImage.x / uImage.y;
  vec2 inset = canvasAspect > imageAspect
    ? vec2(1.0, imageAspect / canvasAspect)
    : vec2(canvasAspect / imageAspect, 1.0);
  return (uv - 0.5) * inset + 0.5;
}

void main() {
  vec2 uv = vUv;
  vec2 aspect = vec2(uCanvas.x / uCanvas.y, 1.0);
  vec2 toPointer = (uv - uPointer) * aspect;
  float dist = length(toPointer);
  vec2 dir = toPointer / max(dist, 1e-5);

  // Presence is the probe resting on the surface; stir is it being moved.
  float presence = smoothstep(uReach, 0.0, dist) * uPointerActive;
  float wake = smoothstep(uReach * 0.73, 0.02, dist) * uStir;

  // The primary bend scales with strength; the ring and the chromatic split grow
  // sub-linearly, so a stronger probe bends further without turning into glitch.
  float accent = sqrt(uStrength);

  // Probe lens: a slight inward pull, so the surface reads as inspected.
  vec2 lens = -dir * presence * 0.012 * uStrength;

  // One travelling ring around a moving probe. Silent the moment it stops.
  vec2 ripple = dir * sin(dist * 21.0 - uTime * 2.6) * wake * 0.0042 * accent;

  // Scroll trail: the surface lags the motion, then settles back.
  vec2 trail = vec2(0.0, uVelocity * 0.016 * (0.3 + uv.y));

  vec2 displaced = uv + lens + ripple + trail;

  // The chromatic edge only exists while the surface is disturbed.
  float disturbance = presence * 0.45 + wake * 0.9 + abs(uVelocity) * 1.3;
  vec2 fringe = dir * disturbance * 0.0026 * accent;

  vec3 photo;
  photo.r = texture(uPhoto, coverUv(displaced + fringe)).r;
  photo.g = texture(uPhoto, coverUv(displaced)).g;
  photo.b = texture(uPhoto, coverUv(displaced - fringe)).b;

  // Match whatever treatment the image carries, so handing the surface over is
  // invisible. The background photograph sits behind a void scrim; the split
  // hero's image stands on its own and passes 1 for both.
  float luma = dot(photo, vec3(0.2126, 0.7152, 0.0722));
  vec3 graded = mix(vec3(luma), photo, uSaturation);
  outColor = vec4(mix(VOID, graded, uVoidBlend), 1.0);
}
`;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Tuning for the two probe modes. Displacement is applied in UV space, so the
 * same value moves fewer pixels on a narrow screen — touch devices therefore get
 * a wider reach and a stronger displacement to read the same as the cursor does.
 */
const REACH = { ambient: 1.5, cursor: 0.82 } as const;
const STRENGTH = { ambient: 2.5, cursor: 1 } as const;

/** The ambient probe's own path, used only when there is no cursor to follow. */
const AMBIENT = { amplitudeX: 0.4, amplitudeY: 0.3, phaseRate: 0.46, stirGain: 5 } as const;

const compile = (gl: WebGL2RenderingContext, type: number, source: string) => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
  gl.deleteShader(shader);
  return null;
};

export const HeroSurface = ({
  className,
  saturation = 0.62,
  voidBlend = 0.72,
}: IHeroSurfaceProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = canvas?.closest<HTMLElement>("[data-hero-root]");
    const photo = root?.querySelector<HTMLImageElement>("[data-hero-media] img");
    if (!canvas || !root || !photo) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      desynchronized: true,
      depth: false,
      stencil: false,
    });
    if (!gl) return;

    // Touch devices have no cursor, so the probe drives itself instead.
    const autonomous = !window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertex || !fragment) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return;
    }
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "aPosition");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uniforms = {
      canvas: gl.getUniformLocation(program, "uCanvas"),
      image: gl.getUniformLocation(program, "uImage"),
      photo: gl.getUniformLocation(program, "uPhoto"),
      pointer: gl.getUniformLocation(program, "uPointer"),
      pointerActive: gl.getUniformLocation(program, "uPointerActive"),
      reach: gl.getUniformLocation(program, "uReach"),
      stir: gl.getUniformLocation(program, "uStir"),
      strength: gl.getUniformLocation(program, "uStrength"),
      saturation: gl.getUniformLocation(program, "uSaturation"),
      time: gl.getUniformLocation(program, "uTime"),
      velocity: gl.getUniformLocation(program, "uVelocity"),
      voidBlend: gl.getUniformLocation(program, "uVoidBlend"),
    };

    const mode = autonomous ? "ambient" : "cursor";
    gl.uniform1f(uniforms.reach, REACH[mode]);
    gl.uniform1f(uniforms.strength, STRENGTH[mode]);
    gl.uniform1f(uniforms.saturation, saturation);
    gl.uniform1f(uniforms.voidBlend, voidBlend);

    const texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.uniform1i(uniforms.photo, 0);

    let frame = 0;
    let disposed = false;
    let visible = true;
    let uploaded = false;
    let hasDrawn = false;
    let ambientTick = false;
    let lastFrame = performance.now();
    let lastScrollY = window.scrollY;
    let time = 0;

    const pointer = { x: 0.5, y: 0.5 };
    const pointerTarget = { x: 0.5, y: 0.5 };
    let pointerActive = 0;
    let pointerActiveTarget = 0;
    let stir = 0;
    let stirTarget = 0;
    let velocity = 0;

    const resize = () => {
      const rect = root.getBoundingClientRect();
      const ratio = clamp(window.devicePixelRatio || 1, 1, 1.5);
      const width = Math.max(1, Math.round(rect.width * ratio));
      const height = Math.max(1, Math.round(rect.height * ratio));
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
      gl.uniform2f(uniforms.canvas, width, height);
      hasDrawn = false;
    };

    const upload = () => {
      if (uploaded || !photo.naturalWidth) return;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, photo);
      gl.uniform2f(uniforms.image, photo.naturalWidth, photo.naturalHeight);
      uploaded = true;
    };

    const handlePointerMove = (event: PointerEvent) => {
      const rect = root.getBoundingClientRect();
      const next = {
        x: clamp((event.clientX - rect.left) / Math.max(1, rect.width), 0, 1),
        y: clamp((event.clientY - rect.top) / Math.max(1, rect.height), 0, 1),
      };
      stirTarget = clamp(
        stirTarget + Math.hypot(next.x - pointerTarget.x, next.y - pointerTarget.y) * 6,
        0,
        1,
      );
      pointerTarget.x = next.x;
      pointerTarget.y = next.y;
      pointerActiveTarget = 1;
    };

    const handlePointerLeave = () => {
      pointerActiveTarget = 0;
      stirTarget = 0;
    };

    const draw = () => {
      frame = 0;
      if (disposed || !visible) return;

      // The ambient probe is slow enough to read at half rate, which keeps
      // touch devices from paying for a frame they cannot perceive.
      if (autonomous) {
        ambientTick = !ambientTick;
        if (ambientTick) {
          frame = requestAnimationFrame(draw);
          return;
        }
      }

      const now = performance.now();
      const delta = Math.min(0.05, (now - lastFrame) / 1000);
      lastFrame = now;
      time += delta;

      const scrollY = window.scrollY;
      const scrolled = (scrollY - lastScrollY) / Math.max(1, window.innerHeight);
      lastScrollY = scrollY;

      if (autonomous) {
        // Two incommensurable frequencies: the path never visibly repeats.
        const phase = time * AMBIENT.phaseRate;
        const previousX = pointerTarget.x;
        const previousY = pointerTarget.y;
        pointerTarget.x = 0.5 + AMBIENT.amplitudeX * Math.sin(phase) * Math.cos(phase * 0.41 + 0.7);
        pointerTarget.y = 0.5 + AMBIENT.amplitudeY * Math.sin(phase * 0.77 + 1.9);
        pointerActiveTarget = 1;

        const speed =
          Math.hypot(pointerTarget.x - previousX, pointerTarget.y - previousY) /
          Math.max(delta, 1e-4);
        stirTarget = clamp(speed * AMBIENT.stirGain, 0, 1);
      }

      const follow = 1 - Math.exp(-delta * 10);
      pointer.x += (pointerTarget.x - pointer.x) * follow;
      pointer.y += (pointerTarget.y - pointer.y) * follow;
      pointerActive += (pointerActiveTarget - pointerActive) * (1 - Math.exp(-delta * 6));

      if (!autonomous) {
        stirTarget *= Math.exp(-delta * 3.4);
        stir += (stirTarget - stir) * (1 - Math.exp(-delta * 7));
      } else {
        stir += (stirTarget - stir) * (1 - Math.exp(-delta * 9));
      }

      const velocityTarget = clamp(scrolled * 9, -1, 1);
      velocity += (velocityTarget - velocity) * (1 - Math.exp(-delta * 13));

      const settled =
        !autonomous &&
        Math.abs(pointer.x - pointerTarget.x) < 0.0004 &&
        Math.abs(pointer.y - pointerTarget.y) < 0.0004 &&
        Math.abs(pointerActive - pointerActiveTarget) < 0.002 &&
        stir < 0.0015 &&
        Math.abs(velocity) < 0.0008;

      if (settled && hasDrawn) {
        frame = requestAnimationFrame(draw);
        return;
      }

      upload();
      if (!uploaded) {
        frame = requestAnimationFrame(draw);
        return;
      }

      gl.uniform2f(uniforms.pointer, pointer.x, pointer.y);
      gl.uniform1f(uniforms.pointerActive, pointerActive);
      gl.uniform1f(uniforms.stir, stir);
      gl.uniform1f(uniforms.velocity, velocity);
      gl.uniform1f(uniforms.time, time);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (!hasDrawn) {
        hasDrawn = true;
        // Hand the surface over only once a real frame exists behind it.
        photo.style.transition = "opacity 600ms ease-out";
        photo.style.opacity = "0";
        canvas.style.opacity = "1";
      }

      frame = requestAnimationFrame(draw);
    };

    const start = () => {
      if (disposed || frame) return;
      lastFrame = performance.now();
      lastScrollY = window.scrollY;
      frame = requestAnimationFrame(draw);
    };

    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      stop();
      // Give the image back rather than leaving a dead surface.
      canvas.style.opacity = "0";
      photo.style.opacity = "";
    };

    resize();
    if (photo.complete && photo.naturalWidth) upload();

    const resizeObserver = new ResizeObserver(() => {
      resize();
      start();
    });
    resizeObserver.observe(root);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
        else stop();
      },
      { rootMargin: "120px" },
    );
    intersectionObserver.observe(root);

    const handleVisibility = () => {
      if (document.hidden) stop();
      else if (visible) start();
    };

    canvas.addEventListener("webglcontextlost", handleContextLost);
    document.addEventListener("visibilitychange", handleVisibility);
    photo.addEventListener("load", upload);

    // The ambient probe owns its own path; a stray touch must not fight it.
    if (!autonomous) {
      root.addEventListener("pointermove", handlePointerMove);
      root.addEventListener("pointerleave", handlePointerLeave);
    }

    start();

    return () => {
      disposed = true;
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      document.removeEventListener("visibilitychange", handleVisibility);
      photo.removeEventListener("load", upload);
      root.removeEventListener("pointermove", handlePointerMove);
      root.removeEventListener("pointerleave", handlePointerLeave);
      photo.style.opacity = "";
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [saturation, voidBlend]);

  return (
    <canvas
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 h-full w-full opacity-0 transition-opacity duration-700",
        className,
      )}
      ref={canvasRef}
    />
  );
};

HeroSurface.displayName = "Content::HeroSurface";
